/**
 * SetOperatorBranches — renderer for top-level UNION / INTERSECT / EXCEPT
 * (Oracle: MINUS) chains. Each branch is rendered via a recursive
 * `<SelectRenderer>` mounted against a synthetic `StatementInfo` covering the
 * branch's byte span, and gets its own derived edit context (per-branch
 * content editing — VQE-3). Separators between branches surface the operator
 * kind (with the `ALL` modifier when present) via `SetOperatorBar`, which
 * becomes a real kind-switch dropdown once the PARENT statement's edit
 * context is ready (§2.3 S7) — the separator itself is addressed through the
 * parent context, not a per-branch one, since a branch's synthetic
 * `statementInfo` can't see the separator between branches. For TOP-LEVEL
 * statements, complex (multi-level parenthesized) precedence is downgraded by
 * `detectRenderMode` before this component is reached; the same detection
 * skips WITH-clause CTE bodies, so a CTE-body mount CAN receive such a shape —
 * the structural scan then treats the parenthesized sub-tree as one opaque
 * branch (same posture as drilled subqueries). The v1 contract is "flat chain
 * only" — see `extractSetOperatorBranches.ts` for the structural scan.
 */
import { Fragment, useMemo } from 'react';

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import { deriveBranchEditContext } from '../../edit/deriveBranchEditContext';
import type { VqeEditContext } from '../../edit/editContext';
import { SetOperatorBar } from './SetOperatorBar';
import type {
  SetOperatorBranchSpan,
  SetOperatorOutline,
} from './extractSetOperatorBranches';
import { makeNestedSubqueryStatement } from './nestedStatement';
import { SelectRenderer } from './SelectRenderer';
import { summarizeSetOperatorChain } from './setOperatorChainSummary';

export interface SetOperatorBranchesProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly outline: SetOperatorOutline;
  readonly onEditAsSql: () => void;
  readonly drillTabId?: string;
  readonly outerAliases?: readonly string[];
  readonly editContext?: VqeEditContext;
  /**
   * Optional per-branch label rendered above each branch card — presentational
   * only. Used by `CteCard` to annotate a recursive CTE body's "Anchor query" /
   * "Recursive step" branches (§2.8 S14, decision 6). A branch with no matching
   * label entry renders no header. Unused by the top-level set-op renderer.
   */
  readonly branchLabels?: readonly string[];
  /**
   * Testid prefix for the branch labels (e.g. `vqe-cte-0`), yielding
   * `${prefix}-branch-label-${idx}`. Required for labels to carry a testid.
   */
  readonly branchLabelTestIdPrefix?: string;
}

export function SetOperatorBranches({
  statement,
  bufferText,
  options,
  outline,
  onEditAsSql,
  drillTabId,
  outerAliases,
  editContext,
  branchLabels,
  branchLabelTestIdPrefix,
}: SetOperatorBranchesProps) {
  if (!outline.hasSetOperators || outline.branches.length === 0) return null;

  // Top-level-only gate: `branchLabels` is set exclusively for CTE
  // recursive-body mounts (see this component's docblock) — its absence
  // cleanly identifies the top-level chain this first-viewport indicator is
  // for (COM-17, COM-30..33). A CTE anchor/recursive-step body must NOT get
  // this header.
  const showChainSummary = !branchLabels?.length;

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="vqe-set-operator-branches"
      data-branch-count={outline.branches.length}
    >
      {showChainSummary ? (
        <div
          className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
          data-testid="vqe-set-operator-chain-summary"
        >
          {summarizeSetOperatorChain(outline, options.engine)}
        </div>
      ) : null}
      {outline.branches.map((branch, idx) => {
        const isLast = idx === outline.branches.length - 1;
        const separator = !isLast ? outline.separators[idx] : null;
        const branchLabel = branchLabels?.[idx];
        return (
          <Fragment key={`branch-${idx}-${branch.start}`}>
            {branchLabel ? (
              <div
                className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                data-testid={
                  branchLabelTestIdPrefix ? `${branchLabelTestIdPrefix}-branch-label-${idx}` : undefined
                }
              >
                {branchLabel}
              </div>
            ) : null}
            <SetOperatorBranch
              branch={branch}
              idx={idx}
              statement={statement}
              bufferText={bufferText}
              options={options}
              onEditAsSql={onEditAsSql}
              drillTabId={drillTabId}
              outerAliases={outerAliases}
              editContext={editContext}
            />
            {separator ? (
              <SetOperatorBar
                separator={separator}
                index={idx}
                options={options}
                editContext={editContext}
              />
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}

interface SetOperatorBranchProps {
  readonly branch: SetOperatorBranchSpan;
  readonly idx: number;
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly onEditAsSql: () => void;
  readonly drillTabId?: string;
  readonly outerAliases?: readonly string[];
  readonly editContext?: VqeEditContext;
}

// One component instance per branch so the per-branch `useMemo` is a stable
// hook call (Rules of Hooks) — it must NOT live inside the parent's
// `branches.map`. `statement.id` is the REAL parent compound id (it's what
// `makeNestedSubqueryStatement` keys the synthetic branch statement on), so
// `deriveBranchEditContext`'s synthetic id matches `synthesized.id` exactly —
// the edit therefore targets the branch actually rendered here.
function SetOperatorBranch({
  branch,
  idx,
  statement,
  bufferText,
  options,
  onEditAsSql,
  drillTabId,
  outerAliases,
  editContext,
}: SetOperatorBranchProps) {
  const synthesized = makeNestedSubqueryStatement(statement.id, branch.start, branch.end);
  // `extractSetOperatorBranches` returns fresh span objects on every parent
  // render, so we key the memo on the stable byte offsets (not the `branch`
  // object identity) — a branch's identity is exactly its `[start, end)` span.
  // `deriveBranchEditContext` re-resolves the Resolver scope synchronously, but
  // the per-tab document is opened asynchronously (see `useVqeEditWiring.ts`'s
  // effect) — on first render it may not be open yet and `resolveScope` throws.
  // Guarded exactly like `useVqeTabDrill`'s drilled edit context.
  const branchEditContext = useMemo(
    () => {
      if (!editContext) return undefined;
      try {
        return deriveBranchEditContext(editContext, statement.id, branch);
      } catch {
        // Document not yet open in service — fall back to no branch-level edit context.
        return undefined;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editContext, statement.id, branch.start, branch.end],
  );
  return (
    <div
      data-testid={`vqe-set-operator-branch-${idx}`}
      data-branch-index={idx}
      className="rounded-lg border border-border bg-background"
    >
      <SelectRenderer
        statement={synthesized}
        bufferText={bufferText}
        options={options}
        onEditAsSql={onEditAsSql}
        drillTabId={drillTabId}
        outerAliases={outerAliases}
        editContext={branchEditContext}
      />
    </div>
  );
}
